import { useEffect, useState } from "react";
import { OrganizationSettings } from "@/features/Organization/components/OrganizationSettings";
import { ModelsSettings } from "@/features/Organization/components/ModelsSettings";
import { SubscriptionSettings } from "@/features/Subscriptions/components/SubscriptionSettings";
import { WorkspaceSettings } from "@/features/Workspaces/components/WorkspaceSettings";
import { UserSettings } from "@/features/Users/components/UserSettings";
import { OrganizationDangerZone } from "@/features/Organization/components/OrganizationDangerZone";
import { FacetsSettings } from "@/features/Facets/components/FacetsSettings";
import { useTranslation } from "react-i18next";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { Navigate, useSearchParams } from "react-router-dom";
import { cn } from "@/infrastructure/lib/utils";

interface SettingsViewProps {
  sidebarOpen?: boolean;
}

const SettingsView = ({ sidebarOpen = false }: SettingsViewProps) => {
  const { t } = useTranslation();
  const { isAdmin, isLoading } = useIsAdmin();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("organization");

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setActiveSection(tab);
    }
  }, [searchParams]);

  if (!isLoading && !isAdmin) {
    return <Navigate to="/access-denied" />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="animate-pulse text-center">
          <p className="text-lg text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  const navItems = [
    { value: "organization", label: t("settings.tabs.organization") },
    { value: "subscription", label: t("settings.tabs.subscription") },
    { value: "models", label: t("settings.tabs.models") },
    { value: "products", label: t("settings.tabs.workspaces") },
    { value: "users", label: t("settings.tabs.users") },
    { value: "facets", label: t("settings.tabs.facets") },
    { value: "dangerZone", label: t("settings.tabs.dangerZone") },
  ];

  return (
    <div className="flex h-full animate-fade-in w-full">
      {/* Sidebar */}
      <aside className={cn("shrink-0 border-r py-4 pr-6 pl-8 transition-[margin,width] duration-300", sidebarOpen ? "w-64" : "ml-[2.5rem] w-[17rem]")}>
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => (
            <button
              key={item.value}
              onClick={() => setActiveSection(item.value)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                activeSection === item.value
                  ? "bg-accent text-accent-foreground font-medium border-l-2 border-primary"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 px-6 py-4">
        {activeSection === "organization" && <OrganizationSettings />}
        {activeSection === "subscription" && <SubscriptionSettings />}
        {activeSection === "models" && <ModelsSettings />}
        {activeSection === "products" && <WorkspaceSettings />}
        {activeSection === "users" && <UserSettings />}
        {activeSection === "facets" && <FacetsSettings />}
        {activeSection === "dangerZone" && <OrganizationDangerZone />}
      </main>
    </div>
  );
};

export default SettingsView;
